import type { ReactNode } from 'react';

import { ThemeProvider as AmplifyThemeProvider, Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { useThemeStore } from '@store/useThemeStore';
import { amplifyTheme } from '@theme/amplifyTheme';
import { signInWithRedirect } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { CircleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';

import { router } from '../router';
import { AUTH_CALLBACK_PATH, getIdentityProviderName } from './configureAmplify';

interface RequireAuthProps {
  children: ReactNode;
}

// IdP outage codes (RFC 6749 §4.1.2.1) get a friendlier message than other failures.
const IDP_OUTAGE_ERROR_CODES = new Set(['server_error', 'temporarily_unavailable']);

const describeRedirectError = (errorCode?: string, fallbackMessage?: string): string =>
  errorCode && IDP_OUTAGE_ERROR_CODES.has(errorCode)
    ? "We can't reach the sign-in provider (KochID) right now. This is usually a temporary outage — please try again shortly."
    : (fallbackMessage ?? 'Sign-in failed. Please try again or contact an admin.');

// Cognito's callback redirect puts failures in the URL (`error`).
const getErrorFromUrl = (): string | undefined => {
  const errorCode = new URLSearchParams(window.location.search).get('error');
  return errorCode ? describeRedirectError(errorCode) : undefined;
};

// Carry the current page through the OAuth round trip via `customState`, so the
// `customOAuthState` Hub event below can send the user back to it post-login
// instead of always landing on the callback route's default redirect. Skipped
// when we're already on the callback route — there's no page worth returning to.
const getReturnPath = (): string | undefined => {
  const { pathname, search } = window.location;
  return pathname === AUTH_CALLBACK_PATH ? undefined : `${pathname}${search}`;
};

// Clear the stale `error` param before retrying — otherwise re-triggering
// sign-in loops straight back into the same error instead of a fresh login.
const startSignIn = () => {
  const returnPath = getReturnPath();
  window.history.replaceState(null, '', window.location.pathname);
  void signInWithRedirect({ customState: returnPath, provider: { custom: getIdentityProviderName() } });
};

// Only KochID (federated OIDC) is supported for this User Pool Client, so
// skip Amplify's default local sign-in form and go straight to the Hosted UI.
const AuthGate = ({ children }: RequireAuthProps) => {
  const { authStatus } = useAuthenticator(context => [context.authStatus]);
  const [redirectError, setRedirectError] = useState<string | undefined>(getErrorFromUrl);

  useEffect(
    () =>
      Hub.listen('auth', ({ payload }) => {
        // Only surfaces via Hub (e.g. the token exchange itself fails), where
        // there's no `error` param in the URL to read.
        if (payload.event === 'signInWithRedirect_failure') {
          const { error } = payload.data;
          setRedirectError(describeRedirectError(undefined, error?.message));
        }
        // The page we captured in `startSignIn`, returned after a successful login.
        if (payload.event === 'customOAuthState' && payload.data) {
          void router.navigate(payload.data);
        }
      }),
    [],
  );

  useEffect(() => {
    if (authStatus === 'unauthenticated' && !redirectError) {
      startSignIn();
    }
  }, [authStatus, redirectError]);

  if (redirectError) {
    return (
      <Box sx={{ alignItems: 'center', display: 'flex', height: '100vh', justifyContent: 'center' }}>
        <Paper sx={{ maxWidth: 420, p: 4, textAlign: 'center' }} variant="outlined">
          <CircleAlert size={32} />
          <Typography sx={{ fontSize: 16, fontWeight: 600, mt: 1.5 }}>Sign-in failed</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
            {redirectError}
          </Typography>
          <Button
            onClick={() => {
              setRedirectError(undefined);
              startSignIn();
            }}
            sx={{ mt: 2.5 }}
            variant="contained"
          >
            Try again
          </Button>
        </Paper>
      </Box>
    );
  }

  if (authStatus !== 'authenticated') {
    return (
      <Box sx={{ alignItems: 'center', display: 'flex', height: '100vh', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return <>{children}</>;
};

export const RequireAuth = ({ children }: RequireAuthProps) => {
  const mode = useThemeStore(state => state.mode);

  return (
    <AmplifyThemeProvider colorMode={mode} theme={amplifyTheme}>
      <Authenticator.Provider>
        <AuthGate>{children}</AuthGate>
      </Authenticator.Provider>
    </AmplifyThemeProvider>
  );
};
